import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useCompany } from "@/contexts/CompanyContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  GraduationCap, Plus, Pencil, MoreVertical, Users, Search, Filter, Eye, Award,
  CalendarCheck, CheckCircle2, FileDown, AlertTriangle, Route, History as HistoryIcon, Trash2,
} from "lucide-react";
import { fmtDate } from "@/lib/format";
import { toast } from "sonner";
import { API_BASE } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";

const STATUS = {
  planejado:     { label: "Planejado", cls: "bg-slate-100 text-slate-700 border-slate-200" },
  agendado:      { label: "Agendado", cls: "bg-blue-100 text-blue-800 border-blue-200" },
  em_andamento:  { label: "Em andamento", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  concluido:     { label: "Concluído", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  cancelado:     { label: "Cancelado", cls: "bg-red-100 text-red-700 border-red-200" },
};

const empty = {
  company_id: "", name: "", tipo: "tecnico", categoria: "", carga_horaria: 8,
  instrutor: "", modalidade: "presencial", data_inicio: "", data_fim: "",
  local: "", publico_alvo: "", obrigatorio: false, validade_meses: null,
  descricao: "", status: "planejado",
};

export default function TrainingsPage() {
  const { companies, companyParam } = useCompany();
  const { hasRole } = useAuth();
  const canEdit = hasRole("rh_admin", "rh_operacional");
  const canAdmin = hasRole("rh_admin");
  const [list, setList] = useState([]);
  const [dash, setDash] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  // Participants
  const [partOpen, setPartOpen] = useState(false);
  const [partTraining, setPartTraining] = useState(null);
  const [enrollments, setEnrollments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [empToAdd, setEmpToAdd] = useState("");

  // Leadership tracks
  const [tab, setTab] = useState("treinamentos");
  const [tracks, setTracks] = useState([]);
  const [trackOpen, setTrackOpen] = useState(false);
  const [trackEditing, setTrackEditing] = useState(null);
  const emptyTrack = { company_id: "", name: "", nivel: "lider", publico_alvo: "",
                       training_ids: [], descricao: "", ativo: true };
  const [trackForm, setTrackForm] = useState(emptyTrack);
  const [trackProgressOpen, setTrackProgressOpen] = useState(false);
  const [trackProgress, setTrackProgress] = useState(null);

  // Employee history
  const [histOpen, setHistOpen] = useState(false);
  const [hist, setHist] = useState(null);
  const [histEmpId, setHistEmpId] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (companyParam) params.company_id = companyParam;
      if (statusFilter !== "all") params.status = statusFilter;
      if (search.trim()) params.search = search.trim();
      const [t, d] = await Promise.all([
        api.get("/trainings", { params }),
        api.get("/trainings/dashboard", { params: companyParam ? { company_id: companyParam } : {} }),
      ]);
      setList(t.data); setDash(d.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [companyParam, statusFilter]);
  useEffect(() => { const t = setTimeout(load, 400); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [search]);

  useEffect(() => {
    api.get("/employees", { params: { page_size: 500, status: "ativo",
      ...(companyParam ? { company_id: companyParam } : {}) } }).then(({ data }) => setEmployees(data.items || []));
  }, [companyParam]);

  const openNew = () => { setEditing(null); setForm({ ...empty, company_id: companyParam || companies[0]?.id || "" }); setOpen(true); };
  const openEdit = (t) => { setEditing(t); setForm({ ...empty, ...t }); setOpen(true); };
  const save = async () => {
    try {
      const payload = {
        ...form,
        carga_horaria: Number(form.carga_horaria || 0),
        validade_meses: form.validade_meses ? Number(form.validade_meses) : null,
      };
      if (editing) await api.put(`/trainings/${editing.id}`, payload);
      else await api.post("/trainings", payload);
      toast.success("Treinamento salvo");
      setOpen(false); load();
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
  };

  const doCancel = async (t) => {
    if (!window.confirm(`Cancelar treinamento "${t.name}"?`)) return;
    try { await api.delete(`/trainings/${t.id}`); toast.success("Cancelado"); load(); }
    catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
  };

  const changeStatus = async (t, status) => {
    try {
      await api.put(`/trainings/${t.id}`, { ...t, status });
      toast.success(`Status → ${STATUS[status].label}`);
      load();
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
  };

  // Participants
  const openParts = async (t) => {
    setPartTraining(t); setPartOpen(true); setEnrollments([]); setEmpToAdd("");
    try {
      const { data } = await api.get(`/trainings/${t.id}/enrollments`);
      setEnrollments(data);
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
  };
  const addPart = async () => {
    if (!empToAdd) return;
    try {
      await api.post(`/trainings/${partTraining.id}/enrollments`, { training_id: partTraining.id, employee_ids: [empToAdd] });
      toast.success("Participante adicionado");
      setEmpToAdd("");
      const { data } = await api.get(`/trainings/${partTraining.id}/enrollments`);
      setEnrollments(data);
      load();
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
  };
  const updateEnrollment = async (eid, patch) => {
    try {
      await api.put(`/enrollments/${eid}`, patch);
      const { data } = await api.get(`/trainings/${partTraining.id}/enrollments`);
      setEnrollments(data);
      load();
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
  };

  // Bulk attendance
  const markAllPresent = async (val) => {
    if (!enrollments.length) return;
    try {
      await api.post(`/trainings/${partTraining.id}/attendance-bulk`, {
        training_id: partTraining.id,
        enrollment_ids: enrollments.map(e => e.id),
        presente: val,
      });
      toast.success(val ? "Todos marcados como presentes" : "Todos marcados como ausentes");
      const { data } = await api.get(`/trainings/${partTraining.id}/enrollments`);
      setEnrollments(data);
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
  };

  // Export
  const exportExcel = async () => {
    try {
      const token = localStorage.getItem("rh_token");
      const url = `${API_BASE}/trainings/export-excel${companyParam ? `?company_id=${companyParam}` : ""}`;
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error();
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "treinamentos.xlsx";
      a.click();
      URL.revokeObjectURL(a.href);
    } catch { toast.error("Falha ao exportar"); }
  };

  // Leadership tracks
  const loadTracks = async () => {
    try {
      const { data } = await api.get("/leadership-tracks",
        { params: companyParam ? { company_id: companyParam } : {} });
      setTracks(data);
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
  };
  useEffect(() => { if (tab === "trilhas") loadTracks(); /* eslint-disable-next-line */ }, [tab, companyParam]);

  const openTrack = (t) => {
    if (t) { setTrackEditing(t); setTrackForm({ ...emptyTrack, ...t }); }
    else { setTrackEditing(null); setTrackForm({ ...emptyTrack, company_id: companyParam || companies[0]?.id || "" }); }
    setTrackOpen(true);
  };
  const saveTrack = async () => {
    try {
      if (trackEditing) await api.put(`/leadership-tracks/${trackEditing.id}`, trackForm);
      else await api.post("/leadership-tracks", trackForm);
      toast.success("Trilha salva");
      setTrackOpen(false);
      loadTracks();
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
  };
  const deleteTrack = async (t) => {
    if (!window.confirm(`Desativar trilha "${t.name}"?`)) return;
    try { await api.delete(`/leadership-tracks/${t.id}`); toast.success("Trilha desativada"); loadTracks(); }
    catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
  };
  const openTrackProgress = async (t) => {
    setTrackProgress(null);
    setTrackProgressOpen(true);
    try {
      const { data } = await api.get(`/leadership-tracks/${t.id}/progress`);
      setTrackProgress(data);
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
  };

  // Employee history
  const openHistory = async () => {
    if (!histEmpId) return;
    setHist(null);
    try {
      const { data } = await api.get(`/employees/${histEmpId}/trainings`);
      setHist(data);
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="trainings-page">
      <div>
        <div className="text-[11px] uppercase tracking-[0.22em] text-[#8A6B2B] font-medium">Desenvolvimento</div>
        <h1 className="font-display text-3xl font-semibold">Treinamentos &amp; Liderança</h1>
        <p className="mt-1 text-[13.5px] text-muted-foreground">Cadastro, turmas, participantes, presença, certificados e trilha de liderança.</p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
        <K label="Total" value={dash?.totals?.total} icon={GraduationCap} tone="primary" />
        <K label="Concluídos" value={dash?.totals?.concluidos} icon={CheckCircle2} tone="success" />
        <K label="Em andamento" value={dash?.totals?.em_andamento} icon={CalendarCheck} tone="warning" />
        <K label="Agendados" value={dash?.totals?.agendados} icon={CalendarCheck} />
        <K label="Participantes" value={dash?.totals?.participantes} icon={Users} />
        <K label="Certificados" value={dash?.totals?.certificados} icon={Award} tone="accent" />
        <K label="Líderes treinados" value={dash?.totals?.lideres_treinados} icon={Users} />
        <K label="Taxa conclusão"
           value={dash?.totals ? `${dash.totals.taxa_conclusao}%` : "—"} icon={CheckCircle2} />
      </div>

      <Tabs value={tab} onValueChange={setTab} data-testid="tr-tabs">
        <TabsList className="bg-white border border-border">
          <TabsTrigger value="treinamentos" data-testid="tab-treinamentos">Treinamentos</TabsTrigger>
          <TabsTrigger value="trilhas" data-testid="tab-trilhas">
            <Route className="h-3.5 w-3.5 mr-1.5" /> Trilha de Liderança
          </TabsTrigger>
          <TabsTrigger value="vencendo" data-testid="tab-vencendo">
            <AlertTriangle className="h-3.5 w-3.5 mr-1.5" /> Certificados vencendo
            {dash?.totals?.certificados_vencendo_60d > 0 &&
              <Badge className="ml-2 bg-amber-500 text-white">{dash.totals.certificados_vencendo_60d}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="historico" data-testid="tab-historico">
            <HistoryIcon className="h-3.5 w-3.5 mr-1.5" /> Histórico por colaborador
          </TabsTrigger>
        </TabsList>

        <TabsContent value="treinamentos" className="mt-4 space-y-4">
        <div className="rounded-lg border border-border bg-white p-4 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Buscar treinamento ou instrutor..." value={search}
                 onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="tr-search" />
        </div>
        <Filter className="h-3.5 w-3.5 text-muted-foreground" />
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-9 w-[160px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos status</SelectItem>
            {Object.keys(STATUS).map(k => <SelectItem key={k} value={k}>{STATUS[k].label}</SelectItem>)}
          </SelectContent>
        </Select>
        {canEdit && <Button onClick={openNew} className="bg-[#234935] hover:bg-[#2c5c43]" data-testid="tr-new-btn"><Plus className="h-4 w-4 mr-2" /> Novo</Button>}
        <Button variant="outline" onClick={exportExcel} data-testid="tr-export-btn">
          <FileDown className="h-3.5 w-3.5 mr-1.5" /> Exportar Excel
        </Button>
      </div>

      <div className="rounded-lg border border-border bg-white overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Treinamento</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead>Instrutor</TableHead>
              <TableHead>CH</TableHead>
              <TableHead>Período</TableHead>
              <TableHead>Participantes</TableHead>
              <TableHead>Status</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? [1,2,3].map(i => <TableRow key={i}><TableCell colSpan={8}><Skeleton className="h-5" /></TableCell></TableRow>) :
             list.length === 0 ? <TableRow><TableCell colSpan={8} className="text-center py-10 text-muted-foreground">Nenhum treinamento cadastrado.</TableCell></TableRow> :
             list.map((t) => {
              const st = STATUS[t.status] || STATUS.planejado;
              return (
                <TableRow key={t.id} className="rh-row" data-testid={`tr-row-${t.id}`}>
                  <TableCell>
                    <div className="font-medium">{t.name}</div>
                    <div className="text-[11px] text-muted-foreground">{t.modalidade} · {t.obrigatorio ? "Obrigatório" : "Opcional"}</div>
                  </TableCell>
                  <TableCell className="text-[12.5px] capitalize">{t.tipo}</TableCell>
                  <TableCell className="text-[12.5px]">{t.instrutor || "—"}</TableCell>
                  <TableCell className="font-mono text-[12.5px]">{t.carga_horaria}h</TableCell>
                  <TableCell className="text-[12.5px]">
                    {t.data_inicio ? fmtDate(t.data_inicio) : "—"}{t.data_fim && ` → ${fmtDate(t.data_fim)}`}
                  </TableCell>
                  <TableCell className="text-[12.5px]">
                    <Badge variant="outline">{t.participantes_count || 0}</Badge>
                    {t.concluidos_count > 0 && <Badge variant="outline" className="ml-1 bg-emerald-50 text-emerald-700 border-emerald-200">{t.concluidos_count} ✓</Badge>}
                  </TableCell>
                  <TableCell><Badge variant="outline" className={`ring-1 ring-inset ${st.cls}`}>{st.label}</Badge></TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button variant="ghost" size="icon"><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onSelect={() => openParts(t)} data-testid={`tr-parts-${t.id}`}><Users className="h-3.5 w-3.5 mr-2" /> Participantes</DropdownMenuItem>
                        {canEdit && <DropdownMenuItem onSelect={() => openEdit(t)}><Pencil className="h-3.5 w-3.5 mr-2" /> Editar</DropdownMenuItem>}
                        <DropdownMenuSeparator />
                        {canEdit && Object.keys(STATUS).filter(s => s !== t.status).map(s => (
                          <DropdownMenuItem key={s} onSelect={() => changeStatus(t, s)}>→ {STATUS[s].label}</DropdownMenuItem>
                        ))}
                        {canAdmin && t.status !== "cancelado" && <DropdownMenuItem onSelect={() => doCancel(t)}>Cancelar/Inativar</DropdownMenuItem>}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              );
             })}
          </TableBody>
        </Table>
      </div>
        </TabsContent>

        <TabsContent value="trilhas" className="mt-4 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-display text-lg font-semibold">Trilhas de Liderança</h2>
              <p className="text-[12.5px] text-muted-foreground">Sequência de treinamentos organizada por nível de liderança.</p>
            </div>
            {canAdmin && (
              <Button onClick={() => openTrack(null)} className="bg-[#234935] hover:bg-[#2c5c43]" data-testid="track-new-btn">
                <Plus className="h-4 w-4 mr-2" /> Nova trilha
              </Button>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="tracks-grid">
            {tracks.length === 0 && (
              <div className="col-span-full text-center py-10 text-muted-foreground text-[13px]">
                Nenhuma trilha cadastrada ainda.
              </div>
            )}
            {tracks.map(tk => (
              <div key={tk.id} className="rounded-lg border border-border bg-white p-5 hover:shadow-md transition-shadow" data-testid={`track-card-${tk.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A6B2B] font-medium">{tk.nivel}</div>
                    <div className="font-display text-[17px] font-semibold mt-0.5 leading-tight">{tk.name}</div>
                  </div>
                  <Route className="h-5 w-5 text-[#C5A059] shrink-0" />
                </div>
                {tk.publico_alvo && <div className="mt-2 text-[12px] text-muted-foreground">{tk.publico_alvo}</div>}
                <div className="mt-4 flex items-center gap-4 text-[12px]">
                  <div><b className="font-mono">{tk.trainings_count}</b> treinamentos</div>
                  <div><b className="font-mono">{tk.carga_total}h</b> carga</div>
                </div>
                <div className="mt-4 flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => openTrackProgress(tk)} data-testid={`track-progress-${tk.id}`}>
                    <Eye className="h-3 w-3 mr-1" /> Progresso
                  </Button>
                  {canAdmin && <Button size="sm" variant="ghost" onClick={() => openTrack(tk)}><Pencil className="h-3 w-3" /></Button>}
                  {canAdmin && <Button size="sm" variant="ghost" onClick={() => deleteTrack(tk)}><Trash2 className="h-3 w-3 text-red-500" /></Button>}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="vencendo" className="mt-4 space-y-4">
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-[13px] text-amber-900 flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <div>
              <b>Certificados vencendo nos próximos 60 dias</b> · Baseado em
              data de conclusão + validade (meses) do treinamento.
            </div>
          </div>
          <div className="rounded-lg border border-border bg-white overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Colaborador</TableHead>
                  <TableHead>Matr.</TableHead>
                  <TableHead>Treinamento</TableHead>
                  <TableHead>Vencimento</TableHead>
                  <TableHead>Dias restantes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(dash?.certificados_expirando || []).length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Nenhum certificado vencendo em breve 🎉</TableCell></TableRow>
                ) : (dash?.certificados_expirando || []).map((c, i) => (
                  <TableRow key={`${c.matricula}-${c.training}-${i}`}>
                    <TableCell className="font-medium">{c.colaborador}</TableCell>
                    <TableCell className="font-mono text-[11px]">{c.matricula}</TableCell>
                    <TableCell>{c.training}</TableCell>
                    <TableCell className="font-mono text-[12px]">{fmtDate(c.vencimento)}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={
                        c.dias_restantes <= 15 ? "bg-red-50 text-red-700 border-red-200"
                        : c.dias_restantes <= 30 ? "bg-amber-50 text-amber-700 border-amber-200"
                        : "bg-emerald-50 text-emerald-700 border-emerald-200"
                      }>{c.dias_restantes} dias</Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="historico" className="mt-4 space-y-4">
          <div className="rounded-lg border border-border bg-white p-4 flex items-center gap-3 flex-wrap">
            <Select value={histEmpId} onValueChange={setHistEmpId}>
              <SelectTrigger className="flex-1 min-w-[260px]" data-testid="hist-select-emp">
                <SelectValue placeholder="Selecione um colaborador" />
              </SelectTrigger>
              <SelectContent>
                {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.nome} · {e.matricula}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button onClick={() => { setHistOpen(true); openHistory(); }} disabled={!histEmpId} className="bg-[#234935] hover:bg-[#2c5c43]" data-testid="hist-open-btn">
              <Eye className="h-4 w-4 mr-2" /> Ver histórico
            </Button>
          </div>
        </TabsContent>
      </Tabs>

      {/* Create/Edit */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Editar" : "Novo"} treinamento</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2"><Label className="text-[11px] uppercase tracking-wider">Nome</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="tr-name" /></div>
            <div><Label className="text-[11px] uppercase tracking-wider">Tipo</Label>
              <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="lideranca">Liderança</SelectItem>
                  <SelectItem value="tecnico">Técnico</SelectItem>
                  <SelectItem value="comportamental">Comportamental</SelectItem>
                  <SelectItem value="obrigatorio">Obrigatório/NR</SelectItem>
                </SelectContent>
              </Select></div>
            <div><Label className="text-[11px] uppercase tracking-wider">Modalidade</Label>
              <Select value={form.modalidade} onValueChange={(v) => setForm({ ...form, modalidade: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="presencial">Presencial</SelectItem>
                  <SelectItem value="online">Online</SelectItem>
                  <SelectItem value="hibrido">Híbrido</SelectItem>
                </SelectContent>
              </Select></div>
            <F label="Categoria" v={form.categoria} s={(v) => setForm({ ...form, categoria: v })} />
            <F label="Instrutor" v={form.instrutor} s={(v) => setForm({ ...form, instrutor: v })} />
            <F label="Carga horária (h)" v={form.carga_horaria} s={(v) => setForm({ ...form, carga_horaria: v })} type="number" />
            <F label="Validade (meses)" v={form.validade_meses} s={(v) => setForm({ ...form, validade_meses: v })} type="number" />
            <F label="Data início" v={form.data_inicio} s={(v) => setForm({ ...form, data_inicio: v })} type="date" />
            <F label="Data fim" v={form.data_fim} s={(v) => setForm({ ...form, data_fim: v })} type="date" />
            <F label="Local" v={form.local} s={(v) => setForm({ ...form, local: v })} />
            <F label="Público-alvo" v={form.publico_alvo} s={(v) => setForm({ ...form, publico_alvo: v })} />
            <div className="flex items-center gap-2 mt-6">
              <Switch checked={form.obrigatorio} onCheckedChange={(v) => setForm({ ...form, obrigatorio: v })} id="obrig" />
              <Label htmlFor="obrig" className="text-[12.5px]">Treinamento obrigatório</Label>
            </div>
            <div className="md:col-span-2">
              <Label className="text-[11px] uppercase tracking-wider">Descrição</Label>
              <Textarea rows={2} value={form.descricao || ""} onChange={(e) => setForm({ ...form, descricao: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button className="bg-[#234935] hover:bg-[#2c5c43]" onClick={save} data-testid="tr-save-btn">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Participants */}
      <Dialog open={partOpen} onOpenChange={setPartOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">Participantes · {partTraining?.name}</DialogTitle>
          </DialogHeader>
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <Select value={empToAdd} onValueChange={setEmpToAdd}>
              <SelectTrigger className="flex-1 min-w-[220px]"><SelectValue placeholder="Selecione um colaborador" /></SelectTrigger>
              <SelectContent>
                {employees.filter(e => !enrollments.find(en => en.employee_id === e.id)).map(e => (
                  <SelectItem key={e.id} value={e.id}>{e.nome} · {e.matricula}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={addPart} disabled={!empToAdd} className="bg-[#234935] hover:bg-[#2c5c43]" data-testid="tr-add-part">
              <Plus className="h-4 w-4 mr-1" /> Adicionar
            </Button>
            {canEdit && enrollments.length > 0 && (
              <>
                <Button size="sm" variant="outline" onClick={() => markAllPresent(true)} data-testid="tr-bulk-present">
                  <CheckCircle2 className="h-3.5 w-3.5 mr-1 text-emerald-600" /> Marcar todos presentes
                </Button>
                <Button size="sm" variant="outline" onClick={() => markAllPresent(false)}>
                  Marcar todos ausentes
                </Button>
              </>
            )}
          </div>
          <div className="rounded-md border border-border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Colaborador</TableHead>
                  <TableHead>Matr.</TableHead>
                  <TableHead>Presente</TableHead>
                  <TableHead>Nota</TableHead>
                  <TableHead>Concluído</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {enrollments.map((en) => (
                  <TableRow key={en.id}>
                    <TableCell className="font-medium">{en.nome}</TableCell>
                    <TableCell className="font-mono text-[11px]">{en.matricula}</TableCell>
                    <TableCell><Switch checked={!!en.presente} onCheckedChange={(v) => updateEnrollment(en.id, { presente: v })} /></TableCell>
                    <TableCell><Input type="number" step="0.1" min="0" max="10" defaultValue={en.nota ?? ""} onBlur={(e) => updateEnrollment(en.id, { nota: e.target.value ? Number(e.target.value) : null })} className="h-7 w-20" /></TableCell>
                    <TableCell><Switch checked={!!en.concluido} onCheckedChange={(v) => updateEnrollment(en.id, { concluido: v })} /></TableCell>
                  </TableRow>
                ))}
                {enrollments.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-6 text-muted-foreground">Ninguém inscrito ainda.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>

      {/* Track form */}
      <Dialog open={trackOpen} onOpenChange={setTrackOpen}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="track-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Route className="h-5 w-5 text-[#C5A059]" /> {trackEditing ? "Editar trilha" : "Nova trilha"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Nome da trilha *</Label>
              <Input value={trackForm.name} onChange={(e) => setTrackForm({ ...trackForm, name: e.target.value })} data-testid="track-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] uppercase tracking-wider">Nível</Label>
                <Select value={trackForm.nivel} onValueChange={(v) => setTrackForm({ ...trackForm, nivel: v })}>
                  <SelectTrigger data-testid="track-nivel"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="lider">Líder</SelectItem>
                    <SelectItem value="supervisor">Supervisor</SelectItem>
                    <SelectItem value="gerente">Gerente</SelectItem>
                    <SelectItem value="diretor">Diretor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider">Público-alvo</Label>
                <Input value={trackForm.publico_alvo || ""} onChange={(e) => setTrackForm({ ...trackForm, publico_alvo: e.target.value })} />
              </div>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Descrição</Label>
              <Textarea rows={2} value={trackForm.descricao || ""} onChange={(e) => setTrackForm({ ...trackForm, descricao: e.target.value })} />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider mb-2 block">Treinamentos da trilha (ordem)</Label>
              <div className="border border-border rounded-md max-h-[220px] overflow-y-auto">
                {list.filter(t => t.tipo === "lideranca" || t.tipo === "comportamental").map(t => {
                  const idx = trackForm.training_ids.indexOf(t.id);
                  const checked = idx >= 0;
                  return (
                    <label key={t.id} className="flex items-center gap-2 px-3 py-1.5 border-b last:border-0 border-border/60 hover:bg-secondary/50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => {
                          const ids = trackForm.training_ids.slice();
                          if (e.target.checked) ids.push(t.id);
                          else ids.splice(ids.indexOf(t.id), 1);
                          setTrackForm({ ...trackForm, training_ids: ids });
                        }}
                        data-testid={`track-tr-${t.id}`}
                      />
                      <span className="flex-1 text-[12.5px]">
                        {checked && <span className="inline-block bg-[#C5A059] text-white text-[10px] font-bold px-1.5 rounded mr-2">{idx + 1}</span>}
                        {t.name}
                      </span>
                      <span className="text-[10.5px] text-muted-foreground">{t.carga_horaria}h</span>
                    </label>
                  );
                })}
                {list.filter(t => t.tipo === "lideranca" || t.tipo === "comportamental").length === 0 && (
                  <div className="p-3 text-[12px] text-muted-foreground">Cadastre treinamentos do tipo <b>Liderança</b> ou <b>Comportamental</b> para compor trilhas.</div>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setTrackOpen(false)}>Cancelar</Button>
            <Button className="bg-[#234935] hover:bg-[#2c5c43]" onClick={saveTrack} data-testid="track-save-btn">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Track progress */}
      <Dialog open={trackProgressOpen} onOpenChange={setTrackProgressOpen}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="track-progress-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Route className="h-5 w-5 text-[#C5A059]" /> Progresso · {trackProgress?.track?.name}
            </DialogTitle>
          </DialogHeader>
          {!trackProgress ? (
            <Skeleton className="h-40 w-full" />
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <K label="Líderes alvo" value={trackProgress.total_target_leaders ?? 0} icon={Users} />
                <K label="Treinamentos" value={trackProgress.total_trainings ?? 0} icon={GraduationCap} />
                <K label="Concluíram todos" value={trackProgress.total_completos ?? 0} icon={CheckCircle2} tone="success" />
                <K label="% conclusão geral" value={`${trackProgress.pct_conclusao_geral ?? 0}%`} icon={Award} tone="accent" />
              </div>
              <div className="rounded-md border border-border overflow-hidden">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Colaborador</TableHead>
                    <TableHead>Matr.</TableHead>
                    <TableHead>Cargo</TableHead>
                    <TableHead className="w-[220px]">Progresso</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {trackProgress.participants.length === 0 && (
                      <TableRow><TableCell colSpan={5} className="text-center py-6 text-muted-foreground">Sem colaboradores no público-alvo.</TableCell></TableRow>
                    )}
                    {trackProgress.participants.map(p => (
                      <TableRow key={p.employee_id}>
                        <TableCell className="font-medium">{p.nome}</TableCell>
                        <TableCell className="font-mono text-[11px]">{p.matricula}</TableCell>
                        <TableCell className="text-[12px]">{p.cargo}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Progress value={p.percentual} className="h-2" />
                            <span className="font-mono text-[11px] w-14 text-right">{p.concluidos}/{p.total}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {p.completed
                            ? <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">✓ Concluída</Badge>
                            : p.percentual > 0
                              ? <Badge variant="outline">Em andamento</Badge>
                              : <Badge variant="outline" className="bg-slate-100 text-slate-600">Não iniciada</Badge>}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Employee history */}
      <Dialog open={histOpen} onOpenChange={setHistOpen}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="hist-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <HistoryIcon className="h-5 w-5" /> Histórico · {hist?.employee?.nome}
            </DialogTitle>
          </DialogHeader>
          {!hist ? <Skeleton className="h-40 w-full" /> : (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <K label="Treinamentos" value={hist.total} icon={GraduationCap} />
                <K label="Concluídos" value={hist.concluidos} icon={CheckCircle2} tone="success" />
                <K label="Horas totais" value={`${hist.horas_total}h`} icon={Award} tone="accent" />
              </div>
              <div className="rounded-md border border-border overflow-hidden">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Treinamento</TableHead>
                    <TableHead>CH</TableHead>
                    <TableHead>Presente</TableHead>
                    <TableHead>Nota</TableHead>
                    <TableHead>Concluído</TableHead>
                    <TableHead>Certificado</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {hist.items.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-6 text-muted-foreground">Nenhum treinamento registrado.</TableCell></TableRow>}
                    {hist.items.map(it => (
                      <TableRow key={it.enrollment_id}>
                        <TableCell className="font-medium">{it.training_name}
                          <div className="text-[10.5px] text-muted-foreground capitalize">{it.training_tipo}</div>
                        </TableCell>
                        <TableCell className="font-mono text-[12px]">{it.carga_horaria}h</TableCell>
                        <TableCell>{it.presente ? "✓" : "—"}</TableCell>
                        <TableCell className="font-mono text-[12px]">{it.nota ?? "—"}</TableCell>
                        <TableCell>
                          {it.concluido
                            ? <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">✓ {fmtDate(it.data_conclusao) || ""}</Badge>
                            : <Badge variant="outline">—</Badge>}
                        </TableCell>
                        <TableCell>
                          {it.status_certificado === "vencido" && <Badge className="bg-red-100 text-red-700 border-red-200">Vencido</Badge>}
                          {it.status_certificado === "proximo_vencimento" && <Badge className="bg-amber-100 text-amber-800 border-amber-200">Vence em breve</Badge>}
                          {it.status_certificado === "valido" && <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">Válido</Badge>}
                          {!it.status_certificado && "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

import DateInputBR from "@/components/DateInputBR";

const F = ({ label, v, s, type = "text" }) => (
  <div><Label className="text-[11px] uppercase tracking-wider">{label}</Label>
    {type === "date"
      ? <DateInputBR value={v} onChange={s} />
      : <Input type={type} value={v ?? ""} onChange={(e) => s(e.target.value)} />}</div>
);
function K({ label, value, icon: Icon, tone = "default" }) {
  const tones = { default: "bg-white", primary: "bg-[#234935] text-white", success: "bg-emerald-50", warning: "bg-amber-50", accent: "bg-[#F9F4E7]" };
  const labelColor = tone === "primary" ? "text-white/70" : "text-muted-foreground";
  const valueColor = tone === "primary" ? "text-white" : "text-foreground";
  return (
    <div className={`rounded-lg border border-border ${tones[tone]} p-3`}>
      <div className="flex items-start justify-between">
        <div>
          <div className={`text-[9.5px] uppercase tracking-[0.18em] font-medium ${labelColor}`}>{label}</div>
          <div className={`mt-1 font-display text-[20px] font-semibold ${valueColor}`}>{value ?? 0}</div>
        </div>
        <Icon className={`h-3.5 w-3.5 ${tone === "primary" ? "text-[#C5A059]" : "text-muted-foreground"}`} />
      </div>
    </div>
  );
}
